import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { applyAccent, getAccent } from "@/lib/accent";
import { useWakeLock } from "@/lib/wakeLock";

// Deferred UI overlays — not needed for first paint, so they're split out of the
// main bundle and loaded lazily (they render nothing until triggered anyway).
const Tour = lazy(() => import("@/components/Tour").then((m) => ({ default: m.Tour })));
const Walkthrough = lazy(() => import("@/components/Walkthrough").then((m) => ({ default: m.Walkthrough })));
const RatePrompt = lazy(() => import("@/components/RatePrompt").then((m) => ({ default: m.RatePrompt })));
const InstallPrompt = lazy(() => import("@/components/InstallPrompt").then((m) => ({ default: m.InstallPrompt })));
const ImageZoom = lazy(() => import("@/components/ImageZoom").then((m) => ({ default: m.ImageZoom })));

// Route-level code splitting — each page ships as its own chunk.
// The thunks are kept so we can PREFETCH every chunk in the background once the
// app is idle, making later tab/link taps feel instant (no loading spinner).
const load = {
  Browse: () => import("@/pages/Browse"),
  Multiview: () => import("@/pages/Multiview"),
  PKBattle: () => import("@/pages/PKBattle"),
  Watch: () => import("@/pages/Watch"),
  Dashboard: () => import("@/pages/Dashboard"),
  Subscriptions: () => import("@/pages/Subscriptions"),
  Premium: () => import("@/pages/Premium"),
  Earnings: () => import("@/pages/Earnings"),
  Revenue: () => import("@/pages/Revenue"),
  Parental: () => import("@/pages/Parental"),
  Profile: () => import("@/pages/Profile"),
  Auth: () => import("@/pages/Auth"),
  ResetPassword: () => import("@/pages/ResetPassword"),
  AuthCallback: () => import("@/pages/AuthCallback"),
  Following: () => import("@/pages/Following"),
  Clips: () => import("@/pages/Clips"),
  Search: () => import("@/pages/Search"),
  Channel: () => import("@/pages/Channel"),
  VodPlayer: () => import("@/pages/VodPlayer"),
  Podcasts: () => import("@/pages/Podcasts"),
  PodcastDetail: () => import("@/pages/PodcastDetail"),
  About: () => import("@/pages/About"),
  Pricing: () => import("@/pages/Pricing"),
  Legal: () => import("@/pages/Legal"),
  Contact: () => import("@/pages/Contact"),
  Admin: () => import("@/pages/Admin"),
  DeleteAccount: () => import("@/pages/DeleteAccount"),
  LiveWatch: () => import("@/pages/LiveWatch"),
  Room: () => import("@/pages/Room"),
  Community: () => import("@/pages/Community"),
  CommunityPost: () => import("@/pages/CommunityPost"),
  Blog: () => import("@/pages/Blog"),
  BlogPost: () => import("@/pages/BlogPost"),
};

const Browse = lazy(load.Browse);
const Multiview = lazy(load.Multiview);
const PKBattle = lazy(load.PKBattle);
const Watch = lazy(load.Watch);
const Dashboard = lazy(load.Dashboard);
const Subscriptions = lazy(load.Subscriptions);
const Premium = lazy(load.Premium);
const Earnings = lazy(load.Earnings);
const Revenue = lazy(load.Revenue);
const Parental = lazy(load.Parental);
const Profile = lazy(load.Profile);
const Auth = lazy(load.Auth);
const ResetPassword = lazy(load.ResetPassword);
const Room = lazy(load.Room);
const AuthCallback = lazy(load.AuthCallback);
const Following = lazy(load.Following);
const Clips = lazy(load.Clips);
const Search = lazy(load.Search);
const Channel = lazy(load.Channel);
const VodPlayer = lazy(load.VodPlayer);
const Podcasts = lazy(load.Podcasts);
const PodcastDetail = lazy(load.PodcastDetail);
const About = lazy(load.About);
const Pricing = lazy(load.Pricing);
const Legal = lazy(load.Legal);
const Contact = lazy(load.Contact);
const Admin = lazy(load.Admin);
const DeleteAccount = lazy(load.DeleteAccount);
const LiveWatch = lazy(load.LiveWatch);
const Community = lazy(load.Community);
const CommunityPost = lazy(load.CommunityPost);
const Blog = lazy(load.Blog);
const BlogPost = lazy(load.BlogPost);

const PageLoader = () => (
  <div className="grid min-h-[60vh] place-items-center" data-testid="page-loader">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/15 border-t-[#00FFC2]" />
  </div>
);

// Old /channel/:name links redirect to the short Kick-style /:name URL.
function ChannelRedirect() {
  const { name } = useParams();
  return <Navigate to={`/${name}`} replace />;
}


function AppRoutes() {
  const location = useLocation();
  // Restore the saved fluorescent accent on load.
  useEffect(() => { applyAccent(getAccent()); }, []);

  // Every navigation should land at the top of the page (footer/nav links etc.)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // SEO: index ONLY substantial pages; noindex thin/dynamic app pages (watch, channel,
  // dashboard, profile, etc.) so Google judges the site on its high-value content
  // (home, blog, guides, about, pricing, policies) — the AdSense "low value" remedy.
  useEffect(() => {
    const p = location.pathname;
    const indexExact = new Set([
      "/", "/about", "/pricing", "/upgrade", "/terms", "/privacy",
      "/guidelines", "/contact", "/community", "/podcasts", "/clips",
      "/multiview", "/pk",
    ]);
    const indexable = indexExact.has(p) || p === "/blog" || p.startsWith("/blog/");
    let el = document.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", indexable ? "index,follow" : "noindex,follow");
  }, [location.pathname]);

  // AdSense policy guard: pause ad requests on content-less pages (login, password
  // reset, account deletion) so Google never serves ads on a screen without publisher content.
  useEffect(() => {
    const contentless = ["/auth", "/reset-password", "/delete-account"];
    const pause = contentless.some((p) => location.pathname.startsWith(p));
    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.pauseAdRequests = pause ? 1 : 0;
    } catch (_) {}
  }, [location.pathname]);

  // Keep the screen awake the whole time the app is open (tour, video, browsing).
  useWakeLock(true);

  // Warm every route chunk in the background so subsequent taps are instant.
  useEffect(() => {
    const prefetch = () => Object.values(load).forEach((fn) => { try { fn(); } catch (_) {} });
    const ric = window.requestIdleCallback;
    const id = ric ? ric(prefetch, { timeout: 2500 }) : setTimeout(prefetch, 1500);
    return () => { (window.cancelIdleCallback || clearTimeout)(id); };
  }, []);

  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }

  const hideFooter = ["/watch", "/dashboard", "/multiview", "/pk", "/clip/", "/vod/", "/search", "/channel/", "/earnings", "/revenue", "/about", "/pricing", "/contact", "/podcast/", "/live/"].some((p) => location.pathname.startsWith(p));

  return (
    <>
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <main key={location.pathname} className="route-fade">
          <Routes>
            <Route path="/" element={<Browse />} />
            <Route path="/multiview" element={<Multiview />} />
            <Route path="/pk" element={<PKBattle />} />
            <Route path="/watch/:id" element={<Watch />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/earnings" element={<Earnings />} />
            <Route path="/revenue" element={<Revenue />} />
            <Route path="/parental" element={<Parental />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/following" element={<Following />} />
            <Route path="/clips" element={<Clips />} />
            <Route path="/search" element={<Search />} />
            <Route path="/channel/:name" element={<ChannelRedirect />} />
            <Route path="/airwave" element={<Navigate to="/aw" replace />} />
            <Route path="/clip/:id" element={<VodPlayer type="clip" />} />
            <Route path="/vod/:id" element={<VodPlayer type="vod" />} />
            <Route path="/podcasts" element={<Podcasts />} />
            <Route path="/podcast/:id" element={<PodcastDetail />} />
            <Route path="/live/:id" element={<LiveWatch />} />
            <Route path="/room/:id" element={<Room />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/:id" element={<CommunityPost />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/upgrade" element={<Pricing />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/guidelines" element={<Legal doc="guidelines" />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/:name" element={<Channel />} />
          </Routes>
        </main>
      </Suspense>
      {!hideFooter && <Footer />}
      <Toaster position="top-right" theme="dark" />
      <Suspense fallback={null}>
        <Tour />
        <Walkthrough />
        <RatePrompt />
        <InstallPrompt />
        <ImageZoom />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
